import { useMemo } from "react";

import { AnyObject } from "@hen/stdlib/types";
import { SegmentedControlItem } from "@mantine/core";
import {
  CellClassParams,
  ColDef,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
} from "ag-grid-community";
import {
  FirstDataRenderedEvent,
  ValueFormatterFunc,
  ValueFormatterParams,
  ValueGetterParams,
  VirtualColumnsChangedEvent,
} from "ag-grid-enterprise";
import _, {
  findIndex,
  get,
  has,
  includes,
  isNaN,
  isNull,
  isNumber,
  replace,
  sortBy,
  sumBy,
} from "lodash";

import {
  ApprovalTree,
  ApprovalTreeWithProposers,
  ApprovalWithLevel,
  ArrayElement,
  BenchmarkThreshold,
  LocalCurrency,
  PROPOSAL_STAGES,
  ProposalWithOwner,
  ProposerBudgetBudgetData,
  ProposerBudgetWithApprovalsAndProposals,
  ProposerBudgetWithApprovalsProposalsAndGrants,
  ProposerBudgetWithProposals,
  Review,
  UserWithCompany,
  ReviewWithCompany,
} from "frontend/utils/types";
import { formatTableDate } from "lib/date";
import { ConvertWithRatesFn } from "lib/frontend/lib/currency";
import { calculateOTEFromUser } from "lib/shared/compensation/calculateOTEFromUser";
import { calculateUserAndProposalCompaRatio } from "lib/shared/compensation/calculateUserAndProposalCompaRatio";
import { annualizeCashValue } from "lib/shared/compensation/cash";
import { EmploymentSubType } from "lib/shared/compensation/employment/types";
import {
  getUserSalaryForInCycleCalculations,
  getInCycleSalaryIncomeFrequency,
  getAnnualizedProposedSalary,
} from "lib/shared/compensation/pay-frequency";
import { getConvertedCurrency } from "lib/shared/currency/getConvertedCurrency";
import { isCompanyAdmin } from "lib/shared/permissions";
import {
  getSuggestedBonus,
  getSuggestedEquity,
  getSuggestedRaise,
  getTotalProposedEquity,
  getBonusTargetInputMode,
} from "lib/shared/proposals";
import { getRangePointValueFromProposal } from "lib/shared/ranges";
import { BudgetMode } from "lib/shared/review/types";
import { ColumnVisibility } from "lib/shared/table-settings";
import {
  Company,
  CompensationApproval,
  CompensationApprovalLevel,
  CompensationApprovalStatus,
  IntegrationProvider,
  PayRangeType,
  PerformanceRating,
  ProposerBudget,
  Range,
  ReviewStatus,
  SalaryBenchmarkType,
  SalaryIncomeFrequency,
  User,
  VariableInputMode,
} from "prisma/cm/client";
import {
  BENCHMARK_THRESHOLD_MAX,
  BENCHMARK_THRESHOLD_MID,
  BENCHMARK_THRESHOLD_MIN,
  COMPENSATION_APPROVAL_STATUS_MAP,
  LOCAL_CURRENCY,
  REVIEW_TAB_BASE_SALARY,
  REVIEW_TAB_BONUS,
  REVIEW_TAB_EQUITY,
  REVIEW_TAB_PROMOTIONS,
  REVIEW_TAB_SALARY,
  REVIEW_TAB_SUMMARY,
  REVIEW_TABS,
  SALARY_INCOME_FREQUENCY_DECIMAL_WHITELIST,
  US_DOLLAR,
} from "utils/constants";

import { Proposal } from "../../../shared/proposals/types";
import { usePrismaObjectSync } from "../store/objectPool/prismaObjectSync";

export const formatBoolean = (value: boolean) => (value ? "Yes" : "No");

export const simpleFormatNumber = (
  value: number,
  locale = "en-US",
  params: Intl.NumberFormatOptions = {
    style: "currency",
    currency: US_DOLLAR,
    currencyDisplay: "symbol",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  },
  convertCents = true,
): string => {
  const cleanValue = !isNumber(value) || Number.isNaN(value) ? 0 : value;
  let formatted = new Intl.NumberFormat(locale, params).format(
    convertCents ? cleanValue / 100 : cleanValue,
  );

  if (locale !== "en-US" && formatted.includes("$")) {
    formatted = formatted.replace("$", `${params.currency} $`);
  }

  return formatted;
};

export const simpleFormatNumberEquity = (value: number): string => {
  return simpleFormatNumber(
    value,
    "en-US",
    { minimumFractionDigits: 0, maximumFractionDigits: 0 },
    false,
  );
};

export function simpleFormatDollars(
  value: number,
  locale?: "en-US",
  params?: Intl.NumberFormatOptions,
): string {
  const convertCents = false;
  return simpleFormatNumber(value, locale, params, convertCents);
}

export function simpleFormatCents(
  value: number,
  locale?: "en-US",
  params?: Intl.NumberFormatOptions,
): string {
  const convertCents = true;
  return simpleFormatNumber(value, locale, params, convertCents);
}

export const currencyValueFormatter = (
  params: ValueFormatterParams,
  settings?: { decimalOverride?: number; decimalField?: string; currencyField?: string },
) => {
  return cellFormatterCurrency(params.context.convert, params.data, params.value, {
    ...settings,
  }).formatted;
};

export const currencyOrNullValueFormatter = (
  params: ValueFormatterParams,
  settings?: { decimalOverride?: number; decimalField?: string; currencyField?: string },
) => {
  if (_.isNil(params.value)) {
    return "";
  }

  return currencyValueFormatter(params, settings);
};

export const userCurrencyValueFormatter = (params: ValueFormatterParams) => {
  return currencyValueFormatter(params, {
    decimalField: "salaryIncomeFrequency",
    currencyField: "salaryCurrencyCode",
  });
};
export const newBonusTargetValueFormatter = (params: ValueFormatterParams) => {
  if (!_.isNumber(params.value)) {
    return params.value;
  }

  if (getBonusTargetInputMode(params.data) === VariableInputMode.DOLLAR) {
    return currencyValueFormatter(params);
  } else {
    return `${params.value?.toFixed(2) ?? 0}%`;
  }
};
export const currentBonusTargetValueFormatter = (params: ValueFormatterParams) => {
  if (!_.isNumber(params.value)) {
    return params.value;
  }

  if (params.data?.owner?.bonusTargetInputMode === VariableInputMode.DOLLAR) {
    return currencyValueFormatter(params);
  } else {
    return `${params.value?.toFixed(2) ?? 0}%`;
  }
};

export const titleCaseFormatter: ValueFormatterFunc = (params: ValueFormatterParams): string => {
  const { value } = params;
  if (_.isNil(value)) {
    return "";
  }

  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const dateTimeToDateFormatter = (params: ValueFormatterParams) => {
  // todo (isaac): we should be consistent with our date formats by using the commented out line below,
  // but the other dates in this specific table use the below format. we should consolidate late
  // return formatDate(params.value)
  return formatTableDate(params.value);
};

export const numberWithCommasFormatter: ColDef["valueFormatter"] = (
  params: ValueFormatterParams,
) => {
  if (_.isNil(params.value)) {
    return "";
  }

  return numberWithCommas(String(params.value));
};

export function numberWithCommas(x: string | number) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const decimalsForProposal = ({
  proposal,
  field = "owner.salaryIncomeFrequency",
  company,
}: {
  proposal: ProposalWithOwner;
  field?: string;
  company?: Company;
}) =>
  proposalNeedsDecimals({
    proposal,
    field,
    company,
  })
    ? 2
    : 0;

export const decimalsForUser = (user: User, field = "salaryIncomeFrequency") => {
  return includes(
    SALARY_INCOME_FREQUENCY_DECIMAL_WHITELIST,
    get(user, field, SalaryIncomeFrequency.YEARLY),
  )
    ? 2
    : 0;
};

export const proposalNeedsDecimals = ({
  proposal,
  field = "owner.salaryIncomeFrequency",
  company,
}: {
  proposal: ProposalWithOwner;
  field?: string;
  company?: Company;
}): boolean => {
  if (company?.alwaysShowDecimals) return true;

  // COM-1006 - % diff issue.
  if (proposal?.raise || proposal?.oteIncrease) {
    const base = proposal?.oteIncrease || proposal?.raise || 0;
    if (base % 1 > 0) return true;
  }

  let inCyclePayFrequencyForEmployee;

  // COM-5421 - possible pay frequency change as part of a cycle
  if (field === "owner.salaryIncomeFrequency" && proposal) {
    inCyclePayFrequencyForEmployee = getInCycleSalaryIncomeFrequency({
      proposalWithOwner: proposal,
    });
  } else {
    inCyclePayFrequencyForEmployee = get(proposal, field, SalaryIncomeFrequency.YEARLY);
  }

  return includes(SALARY_INCOME_FREQUENCY_DECIMAL_WHITELIST, inCyclePayFrequencyForEmployee);
};

// This sets the proper cellClass for Excel based on salary type and input mode
export const cellClassInputModeFormatter = (
  params: CellClassParams,
  inputMode: VariableInputMode,
): string => {
  if (inputMode === VariableInputMode.DOLLAR) {
    return proposalNeedsDecimals({
      proposal: params.node?.data,
    })
      ? "currencyDecimal"
      : "currencyInteger";
  } else {
    return "percent";
  }
};

export const reviewStatusInSetup = (status: Review["status"]): boolean => {
  return status === ReviewStatus.SETUP;
};

export const reviewInSetup = (review: Review): boolean => {
  return reviewStatusInSetup(review.status);
};

export const reviewIsMutable = (review: Review): boolean => {
  const immutableStatus = [ReviewStatus.ENDED];
  // @ts-expect-error TS2345
  return !immutableStatus.includes(review.status);
};

export const reviewEnded = (review: Review): boolean => {
  return review.status === ReviewStatus.ENDED;
};

export const isHRBP = (user: UserWithCompany | null): boolean => {
  if (!user) return false;
  // @ts-expect-error TS2339
  return !!user?.hrSupportRecipients && user?.hrSupportRecipients.length > 0;
};

export const canModifyProposerBudget = (
  review: Review | null,
  proposerBudget: ProposerBudgetWithApprovalsAndProposals | null,
  user: UserWithCompany | null,
): boolean => {
  if (!review || !proposerBudget) return false;

  if (isCompanyAdmin(user)) {
    // company admins can always edit proposerBudgets
    return true;
  }

  // HRBP views can always edit as well; improve detection of HRBP's in the future
  if (isHRBP(user) && review.status !== ReviewStatus.ENDED) {
    return true;
  }

  if (review.status === ReviewStatus.IN_PROGRESS) {
    const currentApproval = getCurrentApproval(proposerBudget.approvals);
    const directApproval = getDirectApproval(proposerBudget.approvals);

    const isSubmitted = proposerBudget.isSubmitted;
    const userIsProposer = user?.id === proposerBudget.proposerId;
    const userIsCurrentApprover = currentApproval && user?.id === currentApproval.approverId;

    //Proposers can only modify proposerBudgets when they're "in proposerBudget" or the direct approval is rejected
    if (
      userIsProposer &&
      (!isSubmitted || directApproval?.status === CompensationApprovalStatus.REJECTED)
    ) {
      return true;
    }

    const activeApprovalStatuses: CompensationApprovalStatus[] = [
      CompensationApprovalStatus.IN_REVIEW,
      CompensationApprovalStatus.PENDING,
    ];

    // Approvers can modify proposer budgets if they are the 'current' and the approval is pending or in review.
    if (
      userIsCurrentApprover &&
      isSubmitted &&
      activeApprovalStatuses.includes(currentApproval.status)
    ) {
      return true;
    }
  }

  return false;
};

export const isOnboarding = (company: any) => {
  if (!company) return false;
  return company.isOnboarding;
};

export const useIsRipplingCompany = (): boolean => {
  const integrations = usePrismaObjectSync.integration.findMany();
  const result = useMemo(() => {
    return _.some(
      integrations,
      (integration) => integration.provider === IntegrationProvider.RIPPLING,
    );
  }, [integrations]);
  return result;
};

const tryNewHomeScreen = true;
export const getDefaultRoute = (user: any) => {
  /**
   * As a historical note, this used to refer to the first link the user would
   * see in the nav, but this was fragile! This rough heuristic preserves the
   * functionality that would have produced at the time.
   */
  if (!user) return "/login";
  if (tryNewHomeScreen) return "/";
  if (isCompanyAdmin(user) && isOnboarding(user?.company)) {
    return "/settings/company-checklist";
  }
  return "/reviews";
};

export const isProposer = (
  proposerBudget: ProposerBudgetWithApprovalsAndProposals | null,
  user: UserWithCompany | null,
): boolean => {
  return !!proposerBudget && !!user && proposerBudget?.proposerId === user?.id;
};

export const isProposerOrAdmin = (
  proposerBudget: ProposerBudgetWithApprovalsAndProposals | null,
  user: UserWithCompany | null,
): boolean => {
  return isProposer(proposerBudget, user) || isCompanyAdmin(user);
};

export const isAnyApprover = (
  proposerBudget: ProposerBudgetWithApprovalsAndProposals | null,
  user: UserWithCompany | null,
): boolean => {
  return (
    !!proposerBudget &&
    !!user &&
    proposerBudget?.approvals.some((approval) => approval.approverId === user?.id)
  );
};

export const isActiveApprover = (
  proposerBudget: ProposerBudgetWithApprovalsAndProposals | null,
  user: UserWithCompany | null,
): boolean => {
  return (
    !!proposerBudget &&
    !!user &&
    proposerBudget?.approvals.some(
      (approval) =>
        approval.status === CompensationApprovalStatus.IN_REVIEW &&
        approval.approverId === user?.id,
    )
  );
};

export const isAnyApproverOrAdmin = (
  proposerBudget: ProposerBudgetWithApprovalsAndProposals | null,
  user: UserWithCompany | null,
): boolean => {
  return isAnyApprover(proposerBudget, user) || isCompanyAdmin(user);
};

export const isActiveApproverOrAdmin = (
  proposerBudget: ProposerBudgetWithApprovalsAndProposals | null,
  user: UserWithCompany | null,
): boolean => {
  return isActiveApprover(proposerBudget, user) || isCompanyAdmin(user);
};

export const isValidRange = (range: Range): boolean => {
  return has(range, "id");
};

export const buildPercentUsageText = (
  budget: number,
  usage: number,
  formatter: (value: number) => { formatted: string },
  showRemaining = true,
  suffix = "used",
): string => {
  if (!isNumber(budget)) {
    return "";
  }

  const remainingBalance = budget - usage;
  const formattedUsage = formatter(usage).formatted;
  const formattedBudget = formatter(budget).formatted;
  const formattedRemaining = formatter(Math.abs(budget - usage)).formatted;

  const baseText = `${formattedUsage} of ${formattedBudget} ${suffix}`;
  const remainingText =
    remainingBalance >= 0 ? ` (${formattedRemaining} left)` : ` (${formattedRemaining} over)`;

  return `${baseText}${showRemaining ? remainingText : ""}`;
};

export const buildPercentReviewedText = (total: number, completed: number): string => {
  const baseText = `${completed} of ${total} employees reviewed`;
  const remainingText = completed !== total ? ` (${total - completed} left)` : "";

  return `${baseText}${remainingText}`;
};

export const filterProposerTabs = (
  review: Review | null,
  proposerBudget?: ProposerBudgetWithApprovalsAndProposals | null,
  user?: UserWithCompany | null,
): SegmentedControlItem[] => {
  if (!review || !proposerBudget || !user) {
    return [];
  }

  const keepTabs = canModifyProposerBudget(review, proposerBudget, user);

  return REVIEW_TABS.filter((tab) => {
    switch (tab) {
      case REVIEW_TAB_PROMOTIONS:
        return keepTabs && review?.enablePromotions;
      case REVIEW_TAB_BASE_SALARY:
        return keepTabs && review?.enableSalary;
      case REVIEW_TAB_SALARY:
        return keepTabs && review?.enableSalary;
      case REVIEW_TAB_BONUS:
        return keepTabs && review?.enableBonus;
      case REVIEW_TAB_EQUITY:
        return keepTabs && review?.enableEquity;
      case REVIEW_TAB_SUMMARY:
        return true;
      default:
        return false;
    }
  });
};

export const getNextTab = (
  currentTab: SegmentedControlItem,
  allTabs: SegmentedControlItem[],
): SegmentedControlItem | null => {
  const currentIndex = findIndex(allTabs, currentTab);

  return get(allTabs, currentIndex + 1, null);
};
export const getPrevTab = (
  currentTab: SegmentedControlItem,
  allTabs: SegmentedControlItem[],
): SegmentedControlItem | null => {
  const currentIndex = findIndex(allTabs, currentTab);

  return get(allTabs, currentIndex - 1, null);
};

export const numberComparator = (value1: number, value2: number): number => {
  // Don't compare nil values - just sort them to the bottom
  if (_.isNil(value1)) return -1;
  if (_.isNil(value2)) return 1;

  return value1 > value2 ? 1 : -1;
};

export const simpleNumberComparator = (regex?: RegExp) => {
  return (value1: string, value2: string): number => {
    const compareA = Number(replace(value1, regex ? regex : /\D/g, ""));
    const compareB = Number(replace(value2, regex ? regex : /\D/g, ""));

    if (compareA === compareB) {
      return 0;
    }

    if (!_.isFinite(compareA)) return -1;
    if (!_.isFinite(compareB)) return 1;
    return compareA > compareB ? 1 : -1;
  };
};

export const rowNodePathComparator = (path: string) => {
  return (
    value1: string,
    value2: string,
    node1: IRowNode<unknown>,
    node2: IRowNode<unknown>,
  ): number => {
    const compareA = get(node1, path, "") || value1;
    const compareB = get(node2, path, "") || value2;
    if (compareA === compareB) return 0;
    return compareA > compareB ? 1 : -1;
  };
};

export const rowNodeCompaRatioBeforeAfterComparator = () => {
  return (
    value1: { beforeRatio: number; afterRatio: number },
    value2: { beforeRatio: number; afterRatio: number },
  ): number => {
    const afterRatio1 = Number(value1.afterRatio);
    const afterRatio2 = Number(value2.afterRatio);

    const compare1 = _.isFinite(afterRatio1) ? afterRatio1 : 0;
    const compare2 = _.isFinite(afterRatio2) ? afterRatio2 : 0;

    // This deals with the case where either afterRatio is a string, we want the blanks to be sorted to the bottom
    if (compare1 === compare2) {
      if (_.isFinite(afterRatio1)) return -1;
      if (_.isFinite(afterRatio2)) return 1;
      return 0;
    }
    if (compare1 === null) return -1;
    if (compare2 === null) return 1;
    return compare1 > compare2 ? 1 : -1;
  };
};

export const calculateSalaryBand = (
  data: Proposal,
  review: Review,
  threshold: BenchmarkThreshold,
  forceHistoricalBenchmark = false,
): number =>
  getRangePointValueFromProposal({
    proposal: data,
    field: review.salaryBenchmarkType === SalaryBenchmarkType.BASE_SALARY ? "salary" : "totalPay",
    target: threshold,
    options: { forceHistoricalBenchmark },
  });

type CompaRatioParams = {
  base: number;
  before: number;
  lowerQuartileFactor: number;
  upperQuartileFactor: number;
  beforeRatio: number;
  afterRatio: number | null;
  tooltip: string | null;
};

export const calculateSalaryCompaRatioParams = (
  params: ICellRendererParams | ValueGetterParams,
  review: ReviewWithCompany,
): CompaRatioParams => {
  const paramData = params.data;
  const incomeFrequency = paramData.owner.salaryIncomeFrequency;
  const rangeIncomeFrequency = paramData.owner.range?.salaryIncomeFrequency ?? incomeFrequency;
  const employmentSubType = paramData.owner.employmentSubType as EmploymentSubType;

  const min = cellFormatterCurrencyBenchmark(
    params.context.convert,
    paramData,
    calculateSalaryBand(paramData, review, BENCHMARK_THRESHOLD_MIN),
  ).raw;

  const minAnnualized = annualizeCashValue({
    value: min,
    incomeFrequency: rangeIncomeFrequency,
    employmentSubType,
  });

  const midAfter = cellFormatterCurrencyBenchmark(
    params.context.convert,
    paramData,
    calculateSalaryBand(paramData, review, BENCHMARK_THRESHOLD_MID),
  ).raw;

  const midAfterAnnualized = annualizeCashValue({
    value: midAfter,
    incomeFrequency: rangeIncomeFrequency,
    employmentSubType,
  });

  const max = cellFormatterCurrencyBenchmark(
    params.context.convert,
    paramData,
    calculateSalaryBand(paramData, review, BENCHMARK_THRESHOLD_MAX),
  ).raw;

  const maxAnnualized = annualizeCashValue({
    value: max,
    incomeFrequency: rangeIncomeFrequency,
    employmentSubType,
  });

  const isOTE = review.salaryBenchmarkType === SalaryBenchmarkType.TOTAL_PAY;
  const includeBonusTarget = getDoesCompanyIncludeBonusTargetInOTECalculation(review.company);

  let before = isOTE
    ? calculateOTEFromUser(paramData.owner, review.company, includeBonusTarget)
    : getSalaryIncreaseBase({ proposal: paramData, annualize: true, company: review.company });

  const convertedBefore = cellFormatterCurrency(params.context.convert, paramData, before).raw;

  const { userCompaRatio, proposalCompaRatio } = calculateUserAndProposalCompaRatio({
    proposal: paramData,
    convert: params.context.convert,
    company: params.context.company,
    review,
  });

  return {
    base: before === Infinity || isNaN(before) ? 0 : midAfter,
    before: convertedBefore,
    lowerQuartileFactor: minAnnualized / midAfterAnnualized,
    upperQuartileFactor: maxAnnualized / midAfterAnnualized,
    tooltip: calculateCompaRatioTooltip(params, `${proposalCompaRatio}`),
    beforeRatio: userCompaRatio ?? 0,
    afterRatio: proposalCompaRatio,
  };
};

export const calculateEquityCompaRatioParams = (
  params: ICellRendererParams | ValueGetterParams,
  review: Review,
): CompaRatioParams => {
  const min = getRangePointValueFromProposal({
    proposal: params.data,
    field: "equity",
    target: BENCHMARK_THRESHOLD_MIN,
  });
  const mid = getRangePointValueFromProposal({
    proposal: params.data,
    field: "equity",
    target: BENCHMARK_THRESHOLD_MID,
  });

  const max = getRangePointValueFromProposal({
    proposal: params.data,
    field: "equity",
    target: BENCHMARK_THRESHOLD_MAX,
  });

  const before = params.data.owner.shares;
  const after = params.data.owner.shares + getTotalProposedEquity(params.data);

  return {
    base: mid,
    before,
    lowerQuartileFactor: min / mid,
    upperQuartileFactor: max / mid,
    tooltip: calculateCompaRatioTooltip(
      params,
      calculateEquityCompaRatioAfter(params, review, true),
    ),
    beforeRatio: mid ? Number((before / mid).toFixed(2)) : 0,
    afterRatio: mid ? Number((after / mid).toFixed(2)) : null,
  };
};

export function formatCompaRatio(compaRatio: number): string {
  return !_.isNumber(compaRatio) || compaRatio === Infinity || isNaN(compaRatio)
    ? ""
    : compaRatio.toFixed(2);
}

type CompaRatioEditabilityContext = {
  company: Company;
  convert: ConvertWithRatesFn;
};

export function getIsCompanyOTE(company?: Company): boolean {
  return company?.payRangeType === PayRangeType.OTE;
}

export function getDoesCompanyIncludeBonusTargetInOTECalculation(company?: Company): boolean {
  return company?.includeBonusTargetInOTECalculation ?? true;
}

export function getDoesRangeIncludeBonusTargetInCompaRatioCalculation(range?: Range): boolean {
  return range?.includeBonusTargetInCompaRatioCalculation ?? true;
}

export function calculateAnnualizedBasePay(user: User, company: Company) {
  const isOTE = getIsCompanyOTE(company);
  const includeBonusTarget = getDoesCompanyIncludeBonusTargetInOTECalculation(company);
  const result = isOTE
    ? calculateOTEFromUser(user, company, includeBonusTarget)
    : annualizeCashValue({
        value: user.salary ?? 0,
        incomeFrequency: user.salaryIncomeFrequency,
        employmentSubType: user.employmentSubType as EmploymentSubType,
        weeklyWorkingHours: company.weeklyWorkingHours,
      });
  return result;
}

export function isEven(n: number) {
  return n % 2 === 0;
}

export function isOdd(n: number) {
  return Math.abs(n % 2) === 1;
}

export function getRangeBasePayPoints(range: Range, company: Company, forceOte?: boolean) {
  const isOTE = forceOte ?? getIsCompanyOTE(company);
  return isOTE ? range?.payPoints?.totalPay : range?.payPoints?.salary;
}

function getMiddlePayPoint(sortedPayPoints: number[]) {
  if (isOdd(sortedPayPoints.length)) return sortedPayPoints[(sortedPayPoints.length - 1) / 2];

  const halfwayIndexLower = Math.floor(sortedPayPoints.length / 2) - 1;
  const halfwayIndexUpper = halfwayIndexLower + 1;

  const halfwayIndexLowerVal = sortedPayPoints[halfwayIndexLower];
  const halfwayIndexUpperVal = sortedPayPoints[halfwayIndexUpper];

  return (halfwayIndexLowerVal + halfwayIndexUpperVal) / 2;
}

function getLegacyRangeLowMidHighCents(range: Range, company: Company) {
  const isOTE = getIsCompanyOTE(company);
  if (isOTE) {
    return {
      // @ts-expect-error TS2551
      rangeBasePayLowCents: range?.totalPayLow ?? 0,
      // @ts-expect-error TS2551
      rangeBasePayMidCents: range?.totalPayMid ?? 0,
      // @ts-expect-error TS2551
      rangeBasePayHighCents: range?.totalPayHigh ?? 0,
    };
  }
  return {
    // @ts-expect-error TS2339
    rangeBasePayLowCents: range?.salaryLow ?? 0,
    // @ts-expect-error TS2339
    rangeBasePayMidCents: range?.salaryMid ?? 0,
    // @ts-expect-error TS2339
    rangeBasePayHighCents: range?.salaryHigh ?? 0,
  };
}

export function getRangeBasePayLowMidHighCents(range: Range, company: Company, forceOTE?: boolean) {
  const basePayPointsObj = getRangeBasePayPoints(range, company, forceOTE);
  if (!basePayPointsObj || _.isEmpty(basePayPointsObj)) {
    return getLegacyRangeLowMidHighCents(range, company);
  }
  const sortedBasePayPoints = _.sortBy(Object.values(basePayPointsObj));

  const rangeBasePayLowCents = sortedBasePayPoints[0] || 0;
  const rangeBasePayMidCents = getMiddlePayPoint(sortedBasePayPoints);
  const rangeBasePayHighCents = sortedBasePayPoints[sortedBasePayPoints.length - 1] || 0;

  return {
    rangeBasePayLowCents,
    rangeBasePayMidCents,
    rangeBasePayHighCents,
  };
}

export function calculateEquityCompaRatioEditability(user: User, range: Range) {
  const totalShares = user.shares;
  // @ts-expect-error TS2339
  return Math.round((totalShares * 100) / range.hireGrantMid) / 100;
}

export function calculateCompaRatioEditability(
  user: User,
  range: Range,
  context: CompaRatioEditabilityContext,
) {
  if (_.isNil(range)) {
    return "";
  }

  const basePay = calculateAnnualizedBasePay(user, context.company);
  const { rangeBasePayMidCents } = getRangeBasePayLowMidHighCents(range, context.company);
  if (!rangeBasePayMidCents || _.isNil(basePay)) return "--";

  const targetCurrencyCode = user.salaryCurrencyCode ?? undefined;

  const formatterParams = {
    decimalField: "salaryIncomeFrequency",
    currencyField: "salaryCurrencyCode",
    sourceCurrency: range.salaryCurrencyCode,
    targetCurrency: targetCurrencyCode,
  };

  const salaryMidConverted = cellFormatterCurrency(
    context.convert,
    // @ts-expect-error TS2345
    range,
    rangeBasePayMidCents,
    formatterParams,
  ).raw;

  const salaryMidConvertedAnnualized = annualizeCashValue({
    value: salaryMidConverted,
    incomeFrequency: range.salaryIncomeFrequency,
    employmentSubType: user.employmentSubType as EmploymentSubType,
  });

  return basePay / salaryMidConvertedAnnualized;
}

export const calculateEquityCompaRatioBefore = (
  params: ValueGetterParams | ICellRendererParams,
  _review: Review,
  forceHistoricalBenchmark = false,
): string => {
  const before = params.data.owner.shares;
  const mid = getRangePointValueFromProposal({
    proposal: params.data,
    field: "equity",
    target: BENCHMARK_THRESHOLD_MID,
    options: {
      forceHistoricalBenchmark,
    },
  });
  // ratio of value to market
  const beforeRatio = before / mid;

  return beforeRatio === Infinity || isNaN(beforeRatio) ? "" : `${beforeRatio.toFixed(2)}`;
};

export const calculateEquityCompaRatioAfter = (
  params: ValueGetterParams | ICellRendererParams,
  _review: Review,
  forceHistoricalBenchmark = false,
): string => {
  const after = params.data.owner.shares + getTotalProposedEquity(params.data);
  const mid = getRangePointValueFromProposal({
    proposal: params.data,
    field: "equity",
    target: BENCHMARK_THRESHOLD_MID,
    options: {
      forceHistoricalBenchmark,
    },
  });

  // ratio of value to market
  const afterRatio = after / mid;

  return afterRatio === Infinity || isNaN(afterRatio) ? "" : `${afterRatio.toFixed(2)}`;
};

export const calculateCompaRatioTooltip = (
  params: ICellRendererParams | ValueGetterParams,
  priorRatio: string,
): string | null => {
  if (!params.data || !params.data.level) return null;

  return params?.data.level.title
    ? `Based on new level of ${params.data.level.title}. ${params.data.owner.name}'s previous Compa Ratio
    (as ${params.data.owner.level.title}) was ${priorRatio}.`
    : null;
};

export const roundOffCentsInteger = (value: number): number => {
  return Math.round(value / 100) * 100;
};

export const getSalaryIncreaseBase = ({
  proposal,
  isOTE = false,
  annualize = false,
  company,
}: {
  proposal: Proposal | ProposalWithOwner;
  isOTE?: boolean;
  annualize?: boolean;
  company?: Company;
}): number => {
  const userSalary = proposal.owner.salary ?? 0;
  const userSalaryForProposal = getUserSalaryForInCycleCalculations({
    proposalWithOwner: proposal,
  });
  const base = isOTE
    ? calculateOTEFromUser(proposal.owner as User, company)
    : annualize
      ? annualizeCashValue({
          value: userSalary,
          incomeFrequency: proposal.owner.salaryIncomeFrequency,
          employmentSubType: proposal.owner.employmentSubType as EmploymentSubType,
          weeklyWorkingHours: company?.weeklyWorkingHours,
        })
      : userSalaryForProposal;

  return Number(base);
};

export const doAppropriateRounding = (
  value: number,
  field: string,
  salaryIncomeFrequency: string,
): number => {
  let roundedResult;
  // let roundingStrategy;
  const decimalFields = ["owner.salary", "salary", "raise", "oteIncrease", "raiseBudget"];
  if (decimalFields.includes(field) && salaryIncomeFrequency === SalaryIncomeFrequency.HOURLY) {
    // roundingStrategy = "roundToNearestCent";
    roundedResult = Math.round(value);
  } else {
    // roundingStrategy = "roundToNearestDollar";
    roundedResult = roundOffCentsInteger(value);
  }
  // console.log({ value, field, salaryIncomeFrequency, roundingStrategy, roundedResult });
  return roundedResult;
};

export const sumEquityBudget = (
  budgets: (
    | ProposerBudgetBudgetData
    | ProposerBudgetWithProposals
    | ProposerBudgetWithApprovalsProposalsAndGrants
    | CompensationApprovalLevel
  )[],
  review?: Review,
): number => {
  if (!review || !budgets?.length) return 0;

  const grantTypeIds = review.grantTypeIds ?? [];

  if (review.equityBudget.mode === BudgetMode.SUM_OF_SUGGESTED) {
    const suggestedSum = _.sumBy(budgets, (budget) => {
      // @ts-expect-error TS2339
      return _.sumBy(budget.proposals, (proposal: Proposal) => {
        return _.sumBy(grantTypeIds, (grantTypeId) => {
          return getSuggestedEquity(proposal, grantTypeId) ?? 0;
        });
      });
    });

    return suggestedSum;
  } else {
    return sumBy(budgets, (budget) => get(budget, "equityBudget", 0));
  }
};

export const addProposerBudgetsToApprovalTree = (
  approvalLevelTree: ApprovalTreeWithProposers,
  proposers: ProposerBudgetWithApprovalsAndProposals[],
  forceDirect = true,
) => {
  approvalLevelTree["proposers"] = proposers.filter((pb) => {
    // find approval with approverId matching this level, and is direct
    return pb.approvals?.find((approval: CompensationApproval) => {
      if (forceDirect) {
        return approval.isDirect && approval.approverId === approvalLevelTree.root.approverId;
      } else {
        return approval.approverId === approvalLevelTree.root.approverId;
      }
    });
  });

  if (approvalLevelTree.children.length) {
    approvalLevelTree.children.forEach((item) => {
      addProposerBudgetsToApprovalTree(item, proposers, forceDirect);
    });
  }
};

export const sumBudgetFromApprovalTree = (
  approvalTree: ApprovalTreeWithProposers,
  field: "raiseBudget" | "bonusBudget" | "equityBudget",
) => {
  if (_.isNumber(approvalTree.root[field])) return approvalTree.root[field];

  let budgetSum = _.sumBy(approvalTree.proposers, field);

  approvalTree.children.forEach((item) => {
    budgetSum +=
      sumBudgetFromApprovalTree(item as unknown as ApprovalTreeWithProposers, field) ?? 0;
  });

  return budgetSum;
};

export const doesApprovalTreeContainNumberBudgetValues = (
  approvalTree: ApprovalTreeWithProposers,
  field: "raiseBudget" | "bonusBudget" | "equityBudget",
): boolean => {
  if (_.isNumber(approvalTree.root[field])) return true;

  if (
    approvalTree.proposers.some((proposer) => {
      return _.isNumber(proposer[field]);
    })
  )
    return true;

  return approvalTree.children.some((item) => {
    return doesApprovalTreeContainNumberBudgetValues(item as ApprovalTreeWithProposers, field);
  });
};

export type SumReviewBudgetBudgetParams = (
  | ProposerBudgetBudgetData
  | ProposerBudgetWithProposals
  | ProposerBudgetWithApprovalsProposalsAndGrants
  | CompensationApprovalLevel
)[];

export type BudgetField = "raiseBudget" | "bonusBudget" | "equityBudget";

export function getReviewBudgetMode(review: Review, field: BudgetField): BudgetMode {
  // @ts-expect-error TS7053
  return review[field].mode;
}

export function isSumOfSuggested(budgetMode: BudgetMode) {
  return budgetMode === BudgetMode.SUM_OF_SUGGESTED;
}

export function getSuggestedBudget({
  proposerBudget,
  field,
  review,
  targetCurrency,
  nanValue,
}: {
  review: Review;
  field: BudgetField;
  proposerBudget: ProposerBudget;
  targetCurrency: string;
  nanValue?: number;
}) {
  const exchangeRates = review?.exchangeRates || {};

  const proposals =
    // @ts-expect-error TS2339
    proposerBudget.proposals?.filter((proposal: Proposal) => proposal.owner) || ([] as Proposal[]);

  const unconvertedValue = sumBy(proposals, (proposal: Proposal) => {
    const sourceCurrency = proposal.owner?.salaryCurrencyCode;

    if (_.isNil(sourceCurrency)) {
      throw new Error("Proposal owner currency is null and no forced source currency");
    }
    // todo(isaac): use the following shared functions for this:
    //  import {
    //   getSumSuggestedBonus,
    //   getSumSuggestedEquity,
    //   getSumSuggestedRaise,
    // } from "lib/shared/proposals";
    let proposalSuggestedUnconvertedValue = null;
    switch (field) {
      case "raiseBudget":
        proposalSuggestedUnconvertedValue = getSuggestedRaise(proposal);
        break;
      case "bonusBudget":
        proposalSuggestedUnconvertedValue = getSuggestedBonus(proposal);
        break;
      case "equityBudget":
        proposalSuggestedUnconvertedValue = getTotalProposedEquity(proposal);
        break;
      default:
        throw new Error("Unsupported budget field");
    }

    if (_.isNaN(proposalSuggestedUnconvertedValue) && !_.isNil(nanValue)) {
      proposalSuggestedUnconvertedValue = nanValue;
    }

    // If we're getting the equity budget, we don't currency convert,
    // But we do need to sum the values across grant types.
    if (field === "equityBudget") {
      // @ts-expect-error TS2345
      return sumBy(Object.values(proposalSuggestedUnconvertedValue), (value) => {
        return value;
      });
    }

    let proposalSuggestedValue = proposalSuggestedUnconvertedValue;

    const proposalSuggestedValueRounded = doAppropriateRounding(
      proposalSuggestedValue,
      field,
      proposal?.owner?.salaryIncomeFrequency ?? SalaryIncomeFrequency.YEARLY,
    );

    // Annualize for raise values (ignoring budget and equity).
    if (field === "raiseBudget") {
      proposalSuggestedValue = annualizeCashValue({
        value: proposalSuggestedValueRounded,
        incomeFrequency: proposal.owner.salaryIncomeFrequency,
        employmentSubType: (proposal.owner.employmentSubType as EmploymentSubType) ?? null,
      });
    }

    if (sourceCurrency !== targetCurrency) {
      proposalSuggestedValue = getConvertedCurrency({
        value: proposalSuggestedValue,
        options: {
          exchangeRates,
          sourceCurrency,
          targetCurrency,
        },
      }).raw;
    }

    return proposalSuggestedValue;
  });

  return unconvertedValue;
}

export const budgetModeForField = (review: Review, budgetField: BudgetField) => {
  // todo: rename salaryBudgetMode to raiseBudgetMode for app-wide consistency - pjc
  const budgetModeKey = budgetField === "raiseBudget" ? "salaryBudgetMode" : `${budgetField}Mode`;
  // @ts-expect-error TS7053
  return (review[budgetModeKey] as BudgetMode) ?? BudgetMode.MANUAL_INPUT;
};

export const salarySuffix = (frequency?: SalaryIncomeFrequency | null): string => {
  switch (frequency) {
    case SalaryIncomeFrequency.HOURLY:
      return "/hr";
    case SalaryIncomeFrequency.DAILY:
      return "/day";
    case SalaryIncomeFrequency.WEEKLY:
      return "/wk";
    case SalaryIncomeFrequency.BI_WEEKLY:
      return "/2 wks";
    case SalaryIncomeFrequency.SEMI_MONTHLY:
      return "/2x mo";
    case SalaryIncomeFrequency.MONTHLY:
      return "/mo";
    case SalaryIncomeFrequency.YEARLY:
    case SalaryIncomeFrequency.FIXED:
    default:
      return "";
  }
};

export const sumAllActiveEquity = (proposals: Proposal[]): number =>
  sumBy(proposals, getTotalProposedEquity);

export const cellFormatterCurrency = (
  currencyConverter: ConvertWithRatesFn,
  compProposal: Proposal,
  value: number,
  settings?: {
    decimalField?: string;
    currencyField?: string;
    decimalOverride?: number;
    sourceCurrency?: string;
    targetCurrency?: string;
  },
) => {
  return currencyConverter(value, {
    decimals:
      settings?.decimalOverride ??
      decimalsForProposal({
        proposal: compProposal,
        field: settings?.decimalField ?? "owner.salaryIncomeFrequency",
      }),
    sourceCurrency:
      settings?.sourceCurrency ??
      get(compProposal, settings?.currencyField ?? "owner.salaryCurrencyCode"),
    targetCurrency:
      settings?.targetCurrency === LOCAL_CURRENCY
        ? get(compProposal, settings?.currencyField ?? "owner.salaryCurrencyCode")
        : settings?.targetCurrency,
  });
};

export const cellFormatterCurrencyBenchmark = (
  currencyConverter: ConvertWithRatesFn,
  compProposal: Proposal,
  value: number,
) => {
  const validRange = isValidRange(compProposal.range as Range);
  return cellFormatterCurrency(
    (value, settings) =>
      currencyConverter(value, {
        ...settings,
        localCurrencyOverride: get(compProposal, "owner.salaryCurrencyCode"),
      }),
    compProposal,
    value,
    {
      decimalField: validRange
        ? "range.salaryIncomeFrequency"
        : "owner.range.salaryIncomeFrequency",
      currencyField: validRange ? "range.salaryCurrencyCode" : "owner.range.salaryCurrencyCode",
    },
  );
};

// remove non numeric chars, but allow for decimal points and negative signs
export const stripNonNumericCharacters = (value: string): string => replace(value, /[^0-9.-]/g, "");
export const getLocalCurrency = (
  currency: string | LocalCurrency,
  compProposal: Proposal,
): string =>
  currency === LOCAL_CURRENCY ? compProposal.owner?.salaryCurrencyCode ?? US_DOLLAR : currency;

export const moveArrayPos = <T>(arr: T[], oldIndex: number, newIndex: number): T[] => {
  const newArray = [...arr];
  newArray.splice(newIndex, 0, newArray.splice(oldIndex, 1)[0]);
  return newArray;
};

export const autoSizeTableColumns = (
  event: FirstDataRenderedEvent | VirtualColumnsChangedEvent | GridReadyEvent,
) => {
  const allColumns = event.api.getColumns();
  if (!allColumns) return;

  const autosizeColumns = allColumns.filter((item) => {
    const def = item.getColDef();
    return !def.suppressAutoSize;
  });

  const autoSizeColumnIds = autosizeColumns.map((item) => item.getColId());
  try {
    event.api.autoSizeColumns(autoSizeColumnIds);
  } catch (e) {
    console.error("Error autosizing columns", e);
  }
};

export const formatOrdinal = (number: number) => {
  // Inspired by: https://stackoverflow.com/a/57518703 and https://v8.dev/features/intl-pluralrules
  const rules = new Intl.PluralRules("en-US", { type: "ordinal" });
  const suffixes = new Map([
    ["one", "st"],
    ["two", "nd"],
    ["few", "rd"],
    ["other", "th"],
  ]);
  const rule = rules.select(number);
  const suffix = suffixes.get(rule);

  return `${number}${suffix}`;
};

// recursively builds a tree from an array of approval levels and a root node
export const buildApprovalTree = (
  allLevels: CompensationApprovalLevel[],
  root: CompensationApprovalLevel,
  depth = 0,
): ApprovalTree => {
  const children = sortBy(
    allLevels.filter((level) => level.parentId === root.approverId),
    "approver.name",
  );

  if (!children.length) {
    return { root: root, depth, parentId: root.approverId, children: [] };
  }

  return {
    root: root,
    depth,
    parentId: root.approverId,
    children: children.map((child) => buildApprovalTree(allLevels, child, depth + 1)),
  };
};

export const getApprovalForUser = (
  approvals?: ApprovalWithLevel[],
  user?: UserWithCompany | null,
) => approvals?.find((approval) => approval.approverId === user?.id);

export const getDirectApproval = (approvals?: ApprovalWithLevel[]) =>
  approvals?.find((approval) => approval.isDirect);

export const getParentApproval = (approvals?: ApprovalWithLevel[], target?: ApprovalWithLevel) =>
  approvals?.find(
    (approval) =>
      approval.approverId ===
      // We maintain both accessor patterns while this utility is used in the context
      // of view models and the Object Pool. When we're no longer mixing use, we can
      // get rid of the "non-explicit" relations.
      // @ts-expect-error TS2551
      (target?.explicitApprovalLevel?.explicitParentId || target?.approvalLevel?.parentId),
  );

export const getFinalApproval = (approvals?: ApprovalWithLevel[]) =>
  approvals?.find(
    (approval) =>
      // We maintain both accessor patterns while this utility is used in the context
      // of view models and the Object Pool. When we're no longer mixing use, we can
      // get rid of the "non-explicit" relations.
      // @ts-expect-error TS2551
      !(approval?.explicitApprovalLevel?.explicitParentId || approval?.approvalLevel?.parentId) &&
      approval.status === CompensationApprovalStatus.APPROVED,
  );

export const getCurrentApproval = (approvals?: ApprovalWithLevel[]) =>
  approvals?.find((approval) => approval.status === CompensationApprovalStatus.IN_REVIEW) ||
  getFinalApproval(approvals) ||
  getDirectApproval(approvals);

export const getApprovalStatusText = (
  approvalStatus?: CompensationApprovalStatus | null,
): string => {
  return (
    COMPENSATION_APPROVAL_STATUS_MAP[approvalStatus as CompensationApprovalStatus] ??
    "Not submitted"
  );
};

export const formatEquity = (value: number) => {
  return { formatted: `${simpleFormatNumberEquity(value)} shares` };
};

export const reduceString = (value: string) => {
  return _.startCase(value.trim());
};

/**
 * These are nullable in that - even though the Range itself may
 * be visible to the user, the specific properties may not.
 *
 * (Note that the value that comes back is `undefined` and not `0`,
 * but AG-Grid will coerce the nil value to a number. for now we
 * assume that no rangepoint should have a value of zero.)
 */

export const redactedSymbol = "—";

export function nullableUserEquityValueFormatter(params: ValueFormatterParams) {
  if (params.value > 0) return simpleFormatNumberEquity(params.value);
  if (params.value === redactedSymbol) return redactedSymbol;

  return "-";
}

export function parseNumberFieldFromCell(params: ValueGetterParams, field: string): number | null {
  const value = get(params.data, field, null);
  if (isNull(value) || value === "") {
    return null;
  }

  return Number(value);
}

export function invertDictionary<T extends string | number, U extends string | number>(
  inputObj: Record<T, U>,
): Record<U, T> {
  const invertedObj: Record<U, T> = {} as Record<U, T>;

  for (const key in inputObj) {
    const value = inputObj[key];
    invertedObj[value] = key as T;
  }

  return invertedObj;
}

export const readableProposalStatus: Record<ArrayElement<typeof PROPOSAL_STAGES>, string> = {
  NOT_SUBMITTED: "Not submitted",
  PENDING_APPROVAL: "Pending approval",
  APPROVED: "Approved",
};

export function sumObjects<T extends Record<string, number>>(objects: T[]): T {
  if (_.isEmpty(objects)) {
    return {} as T;
  }

  // Start with an empty result object
  const summedObject = _.reduce(
    objects,
    (summedObject, obj) => {
      // Only iterate over keys in the current object
      _.forEach(obj, (_value, key) => {
        // Ensure we're dealing with numbers
        const currentValue = summedObject[key] || 0;
        let objValue = obj[key];

        // Skip null or undefined values
        if (objValue === null || objValue === undefined) {
          return;
        }

        // Convert to number if not already
        if (typeof objValue !== "number") {
          objValue = Number(objValue);
          // If conversion failed, log and skip
          if (isNaN(objValue)) {
            console.warn(`Invalid non-numeric value for ${key}:`, obj[key]);
            return;
          }
        }

        // Calculate the result
        const result = currentValue + objValue;

        // Set the result in a type-safe way
        summedObject[key as keyof T] = result as any;
      });

      return summedObject;
    },
    {} as T,
  );

  return summedObject;
}

export const getBonusTargetInCentsForProposal = ({
  proposal,
  isOTE: _isOte = false,
  base,
}: {
  proposal: ProposalWithOwner;
  isOTE?: boolean;
  base?: number;
}) => {
  let bonusTarget;
  //@ts-expect-error TS2345
  if (getBonusTargetInputMode(proposal) === VariableInputMode.DOLLAR) {
    bonusTarget = proposal.bonusTargetNominal ?? proposal.owner?.bonusTargetNominal ?? 0;
  } else {
    const newSalary = base
      ? base
      : // @ts-expect-error TS2345
        getAnnualizedProposedSalary(proposal);

    const bonusTargetPercent =
      proposal.bonusTargetPercent ?? proposal.owner.bonusTargetPercent ?? 0;

    bonusTarget = (bonusTargetPercent / 100) * newSalary;
  }

  return bonusTarget;
};

export const getLatestPerformanceRatingFromList = (
  ratings: PerformanceRating[],
): PerformanceRating | null => {
  if (ratings?.length === 0) return null;

  const sortedRatings = _.sortBy(ratings, "updatedAt").reverse();
  return sortedRatings[0];
};

export const getLatestPerformanceRatingFromSelectedCycles = (
  ratings: PerformanceRating[],
  selectedCycles: string[],
): PerformanceRating | null => {
  if (ratings?.length === 0) return null;

  const ratingsFromSelectedCycles = _.filter(ratings, (rating) =>
    _.includes(selectedCycles, rating.performanceCycleId),
  );

  const sortedRatings = _.sortBy(ratingsFromSelectedCycles, "updatedAt").reverse();
  return sortedRatings[0];
};

export const getDecimalCount = (value: number) => {
  const rightSide = value.toString().split(".")[1] || "";
  return rightSide.length;
};

export function getDiff(
  val: AnyObject,
  b: AnyObject,
  opts = { includeId: true, includeObjects: false },
) {
  return _.pickBy(val, (v, k) => {
    if (_.isArray(v)) {
      return false;
    }

    if (!opts.includeObjects && _.isObject(v)) {
      return false;
    }

    return !_.isEqual(v, b[k]) || (opts.includeId && k === "id");
  });
}

export const getNumberFromString = (value: string) => {
  const regex = /[^0-9]+/g;
  return Number(value.replace(regex, ""));
};

export function shouldHideField(
  visibility: ColumnVisibility | undefined,
  hasAdminVisibility: boolean,
  baseCase = false,
): boolean {
  switch (visibility) {
    case ColumnVisibility.FORBID:
    case ColumnVisibility.ALWAYS_HIDE: {
      return true;
    }
    case ColumnVisibility.ADMIN_ONLY:
    case ColumnVisibility.ADMIN_ONLY_INITIAL_HIDE: {
      return !hasAdminVisibility;
    }
    case ColumnVisibility.INITIAL_HIDE:
    case ColumnVisibility.SHOW: {
      return false;
    }
    default: {
      return baseCase;
    }
  }
}

export const getFormattedPercent = (rawValue: number) => {
  return `${(rawValue * 100).toFixed(2)}%`;
};
